import type { EventType } from "./types.js";

type Emit = (type: EventType, extra?: Record<string, unknown>) => void;

/** Observe the response without awaiting it in, or consuming it from, the commerce path. */
export function observeCartAdds(emit: Emit, allowed: () => boolean, takeContext: () => Record<string, unknown> = () => ({})): void {
  const original = window.fetch;
  window.fetch = function(input, init) {
    const result = original.call(this, input, init);
    try {
      const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, location.href);
      const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
      if (url.origin === location.origin && /\/(?:cart\/add)(?:\.js)?$/.test(url.pathname) && method === "POST" && allowed()) {
        const context = takeContext();
        void result.then(response => {
          if (!response.ok || !allowed()) return;
          return response.clone().json().then((data: { id?: number; items?: Array<{ id?: number }> }) => {
            if (!allowed()) return;
            const items = data.items ?? (data.id ? [data] : []);
            if (!items.length) return;
            emit("add_to_cart", { variant_id: items[0]?.id == null ? undefined : String(items[0].id), metadata: { ...context, source: "confirmed_cart_response", item_count: items.length } });
          });
        }).catch(() => {});
      }
    } catch { /* Observation must never change the original promise or response. */ }
    return result;
  };
}

export function outboundDestination(anchor: HTMLAnchorElement): string | null {
  try {
    const url = new URL(anchor.href, location.href);
    if (url.protocol !== "https:") return null;
    if (url.hostname === "haehae.co" || url.hostname === "www.haehae.co") return "website";
    if (url.hostname === "shopee.sg" || url.hostname.endsWith(".shopee.sg")) return "shopee";
    if (url.hostname === "lazada.sg" || url.hostname.endsWith(".lazada.sg")) return "lazada";
  } catch { /* malformed link: no measurement */ }
  return null;
}

export function observeMarketplaceExits(emit: Emit): void {
  document.addEventListener("click", event => {
    try {
      if (location.pathname !== "/pages/find-us") return;
      const target = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a.shop-link") : null;
      if (!target) return;
      const destination = outboundDestination(target);
      if (destination) emit("marketplace_outbound", { metadata: { destination } });
    } catch { /* Never interfere with the anchor, including nested SVG clicks. */ }
  }, { passive: true, capture: true });
}
